import { useState, useCallback, useEffect } from 'react'
import { Conversation } from '../types'
import * as chatApi from '../services/chatApi'
import { getMyWorkspaces } from '@/features/onboarding/services/workspaceApi'

function mapConversation(r: chatApi.ConversationRecord): Conversation {
  return {
    id: r.id,
    title: r.title,
    lastMessage: '',
    timestamp: r.lastMessageAt ? new Date(r.lastMessageAt) : new Date(r.createdAt),
    messageCount: 0,
    externalThreadId: r.externalThreadId,
  }
}

export function useConversations() {
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null)
  const [workspaceId, setWorkspaceId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function init() {
      try {
        const workspaces = await getMyWorkspaces()
        if (workspaces.length === 0) {
          setError('No workspace found. Please complete onboarding first.')
          return
        }
        const wsId = workspaces[0].id
        setWorkspaceId(wsId)
        const records = await chatApi.listConversations(wsId)
        setConversations(records.map(mapConversation))
      } catch {
        setError('Failed to load conversations.')
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [])

  const createConversation = useCallback(
    async (opts?: { title?: string; externalThreadId?: string }): Promise<string> => {
      if (!workspaceId) throw new Error('No workspace available')
      const record = await chatApi.createConversation(workspaceId, opts)
      const conv = mapConversation(record)
      // Get-or-create: the backend may return an existing conversation for a seed
      // key — de-dupe the local list rather than pushing a second copy.
      setConversations(prev => [conv, ...prev.filter(c => c.id !== conv.id)])
      setActiveConversationId(conv.id)
      return conv.id
    },
    [workspaceId],
  )

  const updateConversation = useCallback(async (id: string, updates: Partial<Conversation>) => {
    // Optimistic local update
    setConversations(prev =>
      prev.map(c => (c.id === id ? { ...c, ...updates } : c))
    )
    // Persist title to API if provided
    if (updates.title) {
      try {
        await chatApi.updateConversation(id, updates.title)
      } catch {
        console.error('Failed to update conversation title')
      }
    }
  }, [])

  const deleteConversation = useCallback(async (id: string) => {
    setConversations(prev => prev.filter(c => c.id !== id))
    setActiveConversationId(prev => (prev === id ? null : prev))
    try {
      await chatApi.deleteConversation(id)
    } catch {
      console.error('Failed to delete conversation')
    }
  }, [])

  return {
    conversations,
    activeConversationId,
    setActiveConversationId,
    createConversation,
    updateConversation,
    deleteConversation,
    workspaceId,
    loading,
    error,
  }
}
